import React from 'react';
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

interface CustomerBudgetData {
  year: string;
  confirmed: number;
  projected: number;
  weighted: number;
  minimum: number;
}

interface CustomerBudgetChartProps {
  customerId: string;
  data: CustomerBudgetData[];
  timeframe: {
    start: string;
    end: string;
    ticks: string[];
  };
  formatBudget: (value: number) => string;
}

export function CustomerBudgetChart({ customerId, data, timeframe, formatBudget }: CustomerBudgetChartProps) {
  const currentYear = new Date().getFullYear().toString();
  const previousYear = (parseInt(currentYear) - 1).toString();

  const filledData = timeframe.ticks.map(year => {
    const existingData = data.find(d => d.year === year);
    const confirmedValue = existingData?.confirmed || 0;
    const projectedValue = existingData?.projected || 0;
    const weightedValue = existingData?.weighted || 0;
    const minimumValue = existingData?.minimum || 0;

    // For years before current year, use confirmed values for all lines
    if (year < currentYear) {
      return {
        year,
        confirmed: confirmedValue,
        projected: confirmedValue,
        weighted: confirmedValue,
        minimum: confirmedValue
      };
    }

    // For current and future years, use projected values
    return {
      year,
      confirmed: confirmedValue,
      projected: projectedValue,
      weighted: weightedValue,
      minimum: minimumValue
    };
  });

  const getClipPathId = (type: string) => `clip-${customerId}-${type}`;

  // Calculate the clip path positions with 10% offset
  const previousYearIndex = timeframe.ticks.indexOf(previousYear);
  const projectedStartPosition = ((previousYearIndex / timeframe.ticks.length) * 100) + 10;
  const projectedWidth = ((timeframe.ticks.length - previousYearIndex) / timeframe.ticks.length) * 100 - 10;

  return (
    <div className="h-32">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={filledData}>
          <defs>
            {/* Clip path for confirmed values (show only up to current year) */}
            <clipPath id={getClipPathId('confirmed')}>
              <rect
                x="0%"
                y="0%"
                width={`${(timeframe.ticks.indexOf(currentYear) / timeframe.ticks.length) * 100}%`}
                height="100%"
              />
            </clipPath>

            {/* Clip path for projected values (show from previous year onwards + 10% offset) */}
            <clipPath id={getClipPathId('projected')}>
              <rect
                x={`${projectedStartPosition}%`}
                y="0%"
                width={`${projectedWidth}%`}
                height="100%"
              />
            </clipPath>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
          <XAxis
            dataKey="year"
            type="category"
            ticks={timeframe.ticks}
            stroke="#9CA3AF"
            fontSize={10}
            tickLine={false}
            axisLine={false}
          />
          <YAxis hide />
          <Tooltip
            contentStyle={{
              backgroundColor: 'white',
              border: 'none',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
            formatter={(value: any) => formatBudget(value)}
            labelFormatter={(year: string) => `Year ${year}`}
          />
          {/* Render minimum line first (in the back) */}
          <Area
            type="monotone"
            dataKey="minimum"
            stroke="#EF4444"
            fill="none"
            strokeWidth={2}
            strokeDasharray="4 4"
            name="Minimum"
            clipPath={`url(#${getClipPathId('projected')})`}
          />
          {/* Render maximum line */}
          <Area
            type="monotone"
            dataKey="projected"
            stroke="#10B981"
            fill="none"
            strokeWidth={2}
            strokeDasharray="4 4"
            name="Maximum"
            clipPath={`url(#${getClipPathId('projected')})`}
          />
          {/* Render projected line */}
          <Area
            type="monotone"
            dataKey="weighted"
            stroke="#F59E0B"
            fill="none"
            strokeWidth={2}
            strokeDasharray="4 4"
            name="Projected"
            clipPath={`url(#${getClipPathId('projected')})`}
          />
          {/* Render confirmed line last (on top) */}
          <Area
            type="monotone"
            dataKey="confirmed"
            stroke="#3B82F6"
            fill="none"
            strokeWidth={2}
            name="Confirmed"
            clipPath={`url(#${getClipPathId('confirmed')})`}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}